import React, { useEffect, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import {
  Calendar, MapPin, Phone, User, CheckCircle2,
  AlertCircle, Building2, Loader2, RefreshCw
} from "lucide-react";
import AuthService from "@/lib/auth";

interface AppointmentInfo {
  encounter_id: string;
  patient_first_name: string;
  provider_name: string;
  encounter_type: string;
  appointment_time: string;
  hospital_name: string;
  branch_name: string;
  full_address: string;
  clinic_phone: string;
  status: string;
  patient_response_status: string;
  patient_responded_at?: string | null;
}

const AppointmentConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const routeParams = useParams();

  const token = routeParams.token || searchParams.get("t") || searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<AppointmentInfo | null>(null);
  const [responseSuccess, setResponseSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("No confirmation token found. Please use the link sent in your SMS reminder.");
      setLoading(false);
      return;
    }

    const fetchInfo = async () => {
      try {
        setLoading(true);
        const data = await AuthService.getAppointmentConfirmInfo(token);
        if (data && (data as any).data) {
          setAppointment((data as any).data);
        } else if (data && data.encounter_id) {
          setAppointment(data);
        } else {
          setError((data as any)?.error || "Appointment details could not be retrieved. The link may have expired.");
        }
      } catch (err: any) {
        setError(err.message || "Failed to load appointment details.");
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [token]);

  const handleResponse = async (action: "confirmed" | "reschedule") => {
    if (!token) return;
    try {
      setSubmitting(true);
      const res = await AuthService.submitAppointmentConfirmation(token, action);
      if (res && ((res as any).data || (res as any).response_status || (res as any).message)) {
        const msg = (res as any).data?.message || (res as any).message || (action === "confirmed" ? "Your attendance has been confirmed!" : "Reschedule request received.");
        setResponseSuccess(msg);
        setAppointment((prev) => prev ? {
          ...prev,
          patient_response_status: action === "confirmed" ? "confirmed" : "reschedule_requested",
        } : null);
      } else {
        setError("Unable to record your response. Please contact the clinic directly.");
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again or call your clinic.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f9f4] via-[#f7f8fa] to-[#eef2f6] flex flex-col items-center justify-center p-4 sm:p-6 font-sans">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden transition-all duration-200">
        
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-[#155e39] via-[#1a7a4a] to-[#24945b] p-6 text-white text-center relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-xs flex items-center justify-center mb-3 shadow-inner">
              <Building2 className="w-6 h-6 text-emerald-100" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
              {appointment?.hospital_name || "MonitorHealth AI"}
            </h1>
            {appointment?.branch_name && appointment.branch_name.toLowerCase() !== appointment.hospital_name.toLowerCase() && (
              <p className="text-emerald-100 text-xs sm:text-sm mt-0.5 font-medium">
                {appointment.branch_name}
              </p>
            )}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-xs text-white mt-3 font-medium">
              <span>Appointment Confirmation</span>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 space-y-6">

          {loading && (
            <div className="py-12 flex flex-col items-center justify-center text-slate-500 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#1a7a4a]" />
              <p className="text-sm font-medium">Loading your appointment details...</p>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-2xl bg-red-50 border border-red-100 p-5 flex items-start gap-3.5 text-red-800">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-900">Notice</h3>
                <p className="text-sm text-red-700 mt-1 leading-relaxed">{error}</p>
                {appointment?.clinic_phone && (
                  <p className="text-xs text-red-600 mt-3 font-medium">
                    Questions? Please call <a href={`tel:${appointment.clinic_phone}`} className="underline font-bold">{appointment.clinic_phone}</a>.
                  </p>
                )}
              </div>
            </div>
          )}

          {!loading && appointment && (
            <>
              {/* Patient Greeting */}
              <div>
                <h2 className="text-lg font-bold text-slate-800 tracking-tight">
                  Hello, {appointment.patient_first_name || "Patient"}!
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Please review and confirm your upcoming appointment.
                </p>
              </div>

              {/* Status Banner if already confirmed or answered */}
              {(appointment.patient_response_status === "confirmed" || responseSuccess) && (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3 text-emerald-800 animate-in fade-in duration-300">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div className="text-xs sm:text-sm">
                    <p className="font-semibold text-emerald-900">Attendance Confirmed</p>
                    <p className="text-emerald-700 mt-0.5">
                      {responseSuccess || "Thank you! We have logged your confirmation. We look forward to seeing you."}
                    </p>
                  </div>
                </div>
              )}

              {appointment.patient_response_status === "reschedule_requested" && !responseSuccess && (
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3 text-amber-800">
                  <RefreshCw className="w-5 h-5 text-amber-600 shrink-0" />
                  <div className="text-xs sm:text-sm">
                    <p className="font-semibold text-amber-900">Reschedule Requested</p>
                    <p className="text-amber-700 mt-0.5">
                      Our clinic staff has been notified and will call you soon to reschedule.
                    </p>
                  </div>
                </div>
              )}

              {/* Details Card */}
              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 sm:p-5 space-y-3.5 text-sm text-slate-700">
                {/* Time */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-2xs border border-slate-100 text-[#1a7a4a] shrink-0 mt-0.5">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                      Date & Time
                    </span>
                    <span className="font-semibold text-slate-900 text-sm sm:text-base">
                      {appointment.appointment_time}
                    </span>
                  </div>
                </div>

                {/* Provider */}
                <div className="flex items-start gap-3 border-t border-slate-200/60 pt-3">
                  <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-2xs border border-slate-100 text-blue-600 shrink-0 mt-0.5">
                    <User className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                      Healthcare Provider
                    </span>
                    <span className="font-semibold text-slate-900">
                      {appointment.provider_name}
                    </span>
                    <span className="text-xs text-slate-500 block mt-0.5">
                      Mode: <span className="font-medium text-slate-700">{appointment.encounter_type}</span>
                    </span>
                  </div>
                </div>

                {/* Location / Address */}
                {appointment.full_address && (
                  <div className="flex items-start gap-3 border-t border-slate-200/60 pt-3">
                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-2xs border border-slate-100 text-amber-600 shrink-0 mt-0.5">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                        Location & Address
                      </span>
                      <span className="font-medium text-slate-800 text-xs sm:text-sm">
                        {appointment.hospital_name}
                        {appointment.branch_name && ` (${appointment.branch_name})`}
                      </span>
                      <span className="text-xs text-slate-500 block mt-0.5 leading-relaxed">
                        {appointment.full_address}
                      </span>
                    </div>
                  </div>
                )}

                {/* Contact Phone */}
                {appointment.clinic_phone && (
                  <div className="flex items-start gap-3 border-t border-slate-200/60 pt-3">
                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-2xs border border-slate-100 text-emerald-600 shrink-0 mt-0.5">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                        Clinic Contact
                      </span>
                      <a
                        href={`tel:${appointment.clinic_phone}`}
                        className="font-semibold text-[#1a7a4a] hover:underline"
                      >
                        {appointment.clinic_phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {appointment.patient_response_status !== "confirmed" && (
                <div className="space-y-3 pt-2">
                  <button
                    disabled={submitting}
                    onClick={() => handleResponse("confirmed")}
                    className="w-full py-3.5 px-4 rounded-2xl bg-[#1a7a4a] hover:bg-[#155e39] text-white font-semibold text-sm sm:text-base flex items-center justify-center gap-2 shadow-md shadow-[#1a7a4a]/25 transition-all duration-150 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Confirm Attendance</span>
                      </>
                    )}
                  </button>

                  <button
                    disabled={submitting}
                    onClick={() => handleResponse("reschedule")}
                    className="w-full py-3 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium text-sm flex items-center justify-center gap-2 transition-all duration-150 disabled:opacity-50"
                  >
                    <RefreshCw className="w-4 h-4 text-slate-400" />
                    <span>Request to Reschedule</span>
                  </button>
                </div>
              )}

              {appointment.patient_response_status === "confirmed" && (
                <div className="text-center pt-2">
                  <p className="text-xs text-slate-400">
                    Need to change this later? Contact your clinic directly at{" "}
                    <a href={`tel:${appointment.clinic_phone}`} className="text-[#1a7a4a] font-semibold underline">
                      {appointment.clinic_phone || "the front desk"}
                    </a>.
                  </p>
                </div>
              )}
            </>
          )}

        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 text-center">
          <p className="text-[11px] text-slate-400">
            Secure appointment service powered by {appointment?.hospital_name || "MonitorHealth AI"} · HIPAA Compliant
          </p>
        </div>

      </div>
    </div>
  );
};

export default AppointmentConfirmation;
